import React from 'react';
import { useIntl } from 'umi';
import { inject, observer } from 'mobx-react';
import { Icon, message } from '@/components';
import { RootStore } from '@/models';
import * as API from '@/api';
import ActionBtn from '../ActionBtn';
import styles from './index.less';
import classNames from 'classnames';

interface Props extends Partial<Pick<RootStore, 'rtc' | 'user'>> {
  RTCInstance?: RootStore['rtc'];
  user?: RootStore['user'];
}
const CameraBtn: React.FC<Props> = inject(({ store }) => {
  return {
    RTCInstance: store.rtc,
    user: store.user,
  };
})(
  observer((props) => {
    const { RTCInstance, user } = props;
    const { formatMessage } = useIntl();

    const openVideo = React.useCallback(async () => {
      if (RTCInstance?.breakLineReconnecting) {
        return;
      }
      if (isPad && RTCInstance?.cameraIsNotAvailable) {
        message.info({
          content: formatMessage({ id: 'NO_DEVICE' }),
          icon: null,
        });
        return;
      }
      if (!RTCInstance?.ableDoubleScreem && RTCInstance?.isShareScreen) {
        message.info({
          content: formatMessage({ id: 'UNABLE_OPEN_VIDEO_ON_SCREEN' }),
          icon: null,
        });
        return;
      }
      try {
        await user?.updateDeviceStatus(
          String(RTCInstance?.cid),
          API.DEVICE_TYPE.VIDEO,
          RTCInstance?.isOpenVideo
            ? API.DEVICE_STATUS_REQUEST.CLOSE
            : API.DEVICE_STATUS_REQUEST.OPEN
        );
        RTCInstance?.isOpenVideo
          ? await RTCInstance.closeCamera()
          : await RTCInstance?.openCamera(true);
      } catch (e) {
        console.error(e);
      }
    }, [RTCInstance]);

    return (
      <ActionBtn
        bottomTitle={
          RTCInstance?.cameraIsNotAvailable
            ? formatMessage({ id: 'CAMERA' })
            : RTCInstance?.isOpenVideo
            ? formatMessage({ id: 'CAMERA_OFF' })
            : formatMessage({ id: 'CAMERA_ON' })
        }
        className={classNames(styles.cameraBtn, {
          [styles.on]: RTCInstance?.isOpenVideo,
          [styles.disable]: RTCInstance?.breakLineReconnecting,
        })}
        onClick={openVideo}
      >
        {RTCInstance?.isOpenVideo && (
          <Icon className={styles.icon} name="icon-video_solid" mode="symbol" />
        )}
        {RTCInstance?.cameraIsNotAvailable && (
          <>
            <Icon className={styles.warn} name="icon-warming_solid" mode="symbol" />
            <Icon className={styles['not-available']} name="icon-video_noequipment" />
          </>
        )}
        {!RTCInstance?.isOpenVideo && !RTCInstance?.cameraIsNotAvailable && (
          <Icon className={styles.icon} name="icon-video_off_solid" mode="symbol" />
        )}
      </ActionBtn>
    );
  })
);

export default CameraBtn;
